import { useNavigate } from 'react-router-dom-v5-compat';
import type { MenuProps } from 'antd';
import { Button, Space, Dropdown, Modal } from 'antd';
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  StopOutlined,
  MoreOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { Simulation } from '../../../store/simulation/types';
import { SimulationExecutionStatus, SimulationStatus } from '../../../store/simulation/types';
import { useStoreState, useStoreActions } from '../../../store';
import { useSession } from '~/hooks/useSession';
import {
  useUpdateSimulation,
  useArchiveSimulation,
  useStartSimulation,
} from '~/hooks/simulation/useSimulationSetupMutations';
import { resolveSimulationServiceVersion } from '~/store/simulation/resolveSimulationServiceVersion';

export interface SimulationActionDropdownProps {
  simulation: Simulation;
}
export const SimulationActionDropdown = ({ simulation }: SimulationActionDropdownProps) => {
  // The row's serviceVersion stamp routes each mutation to the right service (payload stamp wins).
  const updateSimulation = useUpdateSimulation();
  const archiveSimulation = useArchiveSimulation();
  const startSimulation = useStartSimulation();
  const { isShipiumView } = useSession();
  const [modal, contextHolder] = Modal.useModal();
  const sessionPartnerUrl = useStoreState((state) => state.session.user?.sessionPartnerUrl);
  const navigate = useNavigate();
  const fetchSimulationSummaries = useStoreActions((actions) => actions.simulation.fetchSimulationSummaries);
  const fetchSimulationDetail = useStoreActions((actions) => actions.simulation.fetchSimulationDetail);
  const isV2 = resolveSimulationServiceVersion(simulation.simulationSetupData) === 'v2';

  // v1 landing rows live in the EP store (RQ can't refresh them); v2 rows refresh via RQ invalidation.
  const refreshV1List = () => {
    if (!isV2) {
      fetchSimulationSummaries({});
    }
  };

  const handleUpdate = (simulationStatus: SimulationStatus, executionStatus: SimulationExecutionStatus) => {
    updateSimulation
      .mutateAsync({
        simulationId: simulation.simulationId,
        executionStatus,
        simulationStatus,
        simulationSetupData: simulation.simulationSetupData,
      })
      .then(refreshV1List);
  };

  // v2 retry re-runs via start-simulation (the update PUT is inert); v1 keeps its status PUT. Detail
  // is refreshed explicitly since it reads from the EP thunk store, not RQ.
  const handleRetry = () => {
    if (!isV2) {
      handleUpdate(SimulationStatus.PROCESSING, SimulationExecutionStatus.ACTIVE);
      return;
    }
    startSimulation.mutateAsync(simulation).then(() => {
      fetchSimulationDetail(simulation.simulationId);
    });
  };

  const handleArchive = () => {
    archiveSimulation
      .mutateAsync({
        simulationId: simulation.simulationId,
        simulationSetupData: simulation.simulationSetupData,
      })
      .then(refreshV1List);
  };

  const actionItems = (sim: Simulation) => {
    const actionOptions: MenuProps['items'] = [
      {
        key: 'delete_simulation',
        label: (
          <Button
            type={'link'}
            onClick={async () => {
              await modal.confirm({
                title: 'Archive Simulation',
                content: <p>Are you sure you want to archive this simulation? This action is not reversible</p>,
                onOk() {
                  handleArchive();
                  navigate(`/${sessionPartnerUrl}/simulation`);
                },
              });
            }}
          >
            Archive this Simulation <DeleteOutlined />
          </Button>
        ),
      },
    ];

    if (sim.simulationStatus === 'processing') {
      if (sim.executionStatus === SimulationExecutionStatus.ACTIVE) {
        actionOptions.push({
          key: 'pause_simulation',
          label: (
            <Button type={'link'} onClick={() => handleUpdate(sim.simulationStatus, SimulationExecutionStatus.PAUSED)}>
              Pause this Simulation <PauseCircleOutlined />
            </Button>
          ),
        });
      }
      if (sim.executionStatus === 'paused') {
        actionOptions.push({
          key: 'resume_simulation',
          label: (
            <Button type={'link'} onClick={() => handleUpdate(sim.simulationStatus, SimulationExecutionStatus.ACTIVE)}>
              Resume this Simulation <PlayCircleOutlined />
            </Button>
          ),
        });
      }
    }
    if (sim.simulationStatus === SimulationStatus.PROCESSING || sim.simulationStatus === SimulationStatus.FINALIZING) {
      actionOptions.push({
        key: 'cancel_simulation',
        label: (
          <Button type={'link'} onClick={() => handleUpdate(SimulationStatus.CANCELLED, sim.executionStatus)}>
            Cancel this Simulation
            <StopOutlined />
          </Button>
        ),
      });
    }
    if (sim.simulationStatus === SimulationStatus.EXCEPTION && isShipiumView) {
      actionOptions.push({
        key: 'retry_simulation_exception',
        label: (
          <Button type={'link'} onClick={() => handleRetry()}>
            Retry this Simulation
            <PlayCircleOutlined />
          </Button>
        ),
      });
    }
    if (isShipiumView) {
      actionOptions.push({
        key: 'toggle_partner_visibility',
        label: (
          <Button
            type={'link'}
            onClick={() => {
              updateSimulation
                .mutateAsync({
                  simulationId: simulation.simulationId,
                  visibleToPartner: !simulation.visibleToPartner,
                  simulationSetupData: simulation.simulationSetupData,
                })
                .then(refreshV1List);
            }}
          >
            Toggle Partner Visibility
            <EyeOutlined />{' '}
          </Button>
        ),
      });
    }
    return actionOptions;
  };

  const menuItems: MenuProps['items'] = actionItems(simulation).map((ai) => ai);

  const menuProps = {
    items: menuItems,
  };

  return (
    <Space>
      {contextHolder}
      <Dropdown menu={menuProps} trigger={['click']}>
        <Button icon={<MoreOutlined color={'#000'} />}></Button>
      </Dropdown>
    </Space>
  );
};
